import { useRef, useMemo, useState, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Plus, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { EmployeeCard } from "./EmployeeCard";
import { SelectInput } from "./fields/SelectInput";
import { isEmployeeIncomplete } from "../validation/rules";
import type { Employee } from "../types/form";

interface EmployeeListProps {
  employees: Employee[];
  offersDental: boolean;
  offersVision: boolean;
  availablePlans: number;
  showErrors?: boolean;
  errorMessage?: string | null;
  onAdd?: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  actionsDisabled?: boolean;
}

const ESTIMATED_CARD_HEIGHT = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, "All"] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export function EmployeeList({
  employees,
  offersDental,
  offersVision,
  availablePlans,
  showErrors,
  errorMessage,
  onAdd,
  onEdit,
  onDelete,
  actionsDisabled,
}: EmployeeListProps) {
  // TanStack Virtual's useVirtualizer returns functions React Compiler can't safely
  // memoize. Without this directive the compiler skips memoizing the whole component.
  "use no memo";
  const parentRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(25);

  const sorted = useMemo(
    () =>
      employees
        .map((e, i) => ({ employee: e, originalIndex: i }))
        .sort((a, b) => {
          const last = a.employee.lastName.localeCompare(b.employee.lastName);
          return last !== 0 ? last : a.employee.firstName.localeCompare(b.employee.firstName);
        }),
    [employees],
  );

  const effectivePageSize = pageSize === "All" ? sorted.length : pageSize;
  const totalPages = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * effectivePageSize;
  const pageItems = sorted.slice(pageStart, pageStart + effectivePageSize);

  // eslint-disable-next-line react-hooks/incompatible-library -- handled by the "use no memo" directive above
  const virtualizer = useVirtualizer({
    count: pageItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    overscan: 5,
  });

  const handlePageSizeChange = (size: PageSize) => {
    setPageSize(size);
    setPage(0);
    parentRef.current?.scrollTo({ top: 0 });
  };

  const [fading, setFading] = useState(false);
  const prevCountRef = useRef(employees.length);

  const handlePageChange = (newPage: number) => {
    setFading(true);
    requestAnimationFrame(() => {
      setPage(newPage);
    });
  };

  // After page data swaps: scroll to top, then fade in.
  // Depend on safePage (a stable primitive) — pageItems is a fresh array each render
  // and would fire this effect every render.
  useEffect(() => {
    if (fading) {
      parentRef.current?.scrollTo({ top: 0 });
      const id = requestAnimationFrame(() => setFading(false));
      return () => cancelAnimationFrame(id);
    }
  }, [safePage, fading]);

  // Brief fade + page reset when employees change (add/remove/census upload)
  useEffect(() => {
    if (employees.length !== prevCountRef.current) {
      const countChanged = Math.abs(employees.length - prevCountRef.current);
      prevCountRef.current = employees.length;
      // Reset to page 0 on bulk changes (census upload replaces all)
      if (countChanged > 1) setPage(0);
      setFading(true);
      requestAnimationFrame(() => setFading(false));
    }
  }, [employees.length]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700" aria-live="polite">
          {employees.length} employee{employees.length !== 1 ? "s" : ""} added
        </span>
        <button
          onClick={onAdd}
          disabled={!onAdd}
          aria-label="Add employee"
          className={`flex items-center gap-1 px-3.5 py-1.5 text-[13px] font-semibold rounded-[10px] border-[1.5px] border-dashed transition-colors ${onAdd ? "text-brand-green border-brand-green hover:bg-brand-green-05 cursor-pointer" : "text-gray-400 border-gray-200 cursor-default"}`}
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      {errorMessage && (
        <div className="flex items-start gap-3 bg-danger-bg border border-danger rounded-lg p-4 mb-3 animate-fade-in" role="alert">
          <AlertTriangle size={18} className="text-danger shrink-0 mt-0.5" />
          <p className="text-sm text-gray-800">{errorMessage}</p>
        </div>
      )}

      {pageItems.length > 0 && (
        <div
          ref={parentRef}
          className={`overflow-y-auto transition-opacity duration-150 ease-in-out ${fading ? "opacity-0" : "opacity-100"}`}
          style={{ maxHeight: "min(500px, 50vh)" }}
        >
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const { employee, originalIndex } = pageItems[virtualRow.index];
              // Key on uid — array index re-keys every following row on remove, which
              // breaks virtualizer reconciliation and animation.
              return (
                <div
                  key={employee.uid ?? `idx-${originalIndex}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                >
                  <div className="pb-2">
                    <EmployeeCard
                      employee={employee}
                      offersDental={offersDental}
                      offersVision={offersVision}
                      availablePlans={availablePlans}
                      onEdit={() => onEdit(originalIndex)}
                      onDelete={() => onDelete(originalIndex)}
                      actionsDisabled={actionsDisabled}
                      hasError={showErrors && isEmployeeIncomplete(employee, availablePlans, offersDental, offersVision)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-xs pt-3 mt-1 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <SelectInput
              label="Show"
              value={String(pageSize)}
              onChange={(v) => handlePageSizeChange(v === "All" ? "All" : Number(v) as PageSize)}
              options={PAGE_SIZE_OPTIONS.map((s) => ({ value: String(s), label: String(s) }))}
              containerClassName="flex items-center gap-1.5"
              hideCheck
              hidePlaceholder
              size="sm"
            />
          </div>

          {/* Page navigation — compact on mobile, full on desktop */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(safePage - 1)}
                disabled={safePage === 0}
                className="px-1.5 py-1.5 rounded-[10px] hover:bg-gray-100 active:scale-95 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-all duration-150"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="sm:hidden text-gray-500 px-1">
                {safePage + 1} / {totalPages}
              </span>
              <div className="hidden sm:flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => {
                  const show = i === 0 || i === totalPages - 1 || Math.abs(i - safePage) <= 1;
                  const prevShown = i > 0 && (i - 1 === 0 || i - 1 === totalPages - 1 || Math.abs(i - 1 - safePage) <= 1);
                  if (!show) return !prevShown ? null : <span key={i} className="px-1 text-gray-300">…</span>;
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      aria-label={`Page ${i + 1}`}
                      aria-current={i === safePage ? "page" : undefined}
                      className={`w-8 h-8 flex items-center justify-center rounded-[10px] text-[13px] cursor-pointer ${
                        i === safePage
                          ? "bg-brand-green text-white font-semibold shadow-sm"
                          : "text-gray-500 hover:bg-gray-100 transition-colors duration-150"
                      }`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(safePage + 1)}
                disabled={safePage >= totalPages - 1}
                className="px-1.5 py-1.5 rounded-[10px] hover:bg-gray-100 active:scale-95 disabled:opacity-30 disabled:cursor-default cursor-pointer transition-all duration-150"
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
